import { debouncePromise } from "@components/layouts/navigation/search/debouncedUtil";
import type { StoreLocatorLocation } from "@components/store-locator/controller";
import { activeSiteConfig } from "@config/site/site-config";
import { Logger, ServiceType, LogTag } from "@lib/monitoring/logger";

const { country } = activeSiteConfig;

const DEBOUNCE_MS = 100;

const fetchGeocodeResults = async (query: string, restrictToCurrentMarket: boolean = false) => {
    if (!query.trim()) {
        return [];
    }

    const request: google.maps.GeocoderRequest = {
        address: query,
        region: country,
    };

    // Add componentRestrictions to filter results to current market
    if (restrictToCurrentMarket) {
        request.componentRestrictions = {
            country: country,
        };
    }

    try {
        const GeoCoder = new google.maps.Geocoder();
        const response = await GeoCoder.geocode(request);
        return response.results;
    } catch (error) {
        Logger.error(ServiceType.GOOGLE, "Google Geocoding API error", {
            tag: LogTag.STORES,
            error: error instanceof Error ? error.message : String(error),
            query_length: query.length,
            country,
        });
        return [];
    }
};

// Debounced geocode function
const debouncedFetchGeocodeResults = debouncePromise(fetchGeocodeResults, DEBOUNCE_MS);

// Local cache for geocode results to avoid redundant API calls for the same query
const geocodeCache = new Map<string, google.maps.GeocoderResult[]>();

export const getGeocodeResults = async (
    query: string,
    restrictToCurrentMarket: boolean = false,
    location?: StoreLocatorLocation,
    // Debouncing is for typing: a superseded call never settles, so lookups made together
    // from saved addresses (not keystrokes) have to opt out or all but the last one hang.
    { debounce = true }: { debounce?: boolean } = {}
): Promise<google.maps.GeocoderResult[]> => {
    const cacheKey = `${query}-${country}-${restrictToCurrentMarket}`;
    const logPayload = {
        tag: LogTag.STORES,
        query_length: query.length,
        location: location || "unknown",
        country,
    };

    if (geocodeCache.has(cacheKey)) {
        const result = geocodeCache.get(cacheKey);
        if (result) {
            Logger.info(ServiceType.GOOGLE, "Google Geocoding cache hit", {
                ...logPayload,
                isCached: true,
            });
            return Promise.resolve(result);
        }
    }

    const results = await (debounce ? debouncedFetchGeocodeResults : fetchGeocodeResults)(
        query,
        restrictToCurrentMarket
    );
    geocodeCache.set(cacheKey, results);

    Logger.info(ServiceType.GOOGLE, "Google Geocoding API call", {
        ...logPayload,
        isCached: false,
        results_count: results.length,
    });

    return results;
};
